import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth, useLanguage } from "@/context/AppProviders";
import { api, formatApiError } from "@/lib/api";
import { FadeIn, Overline } from "@/components/Kinetic";
import { CheckCircle2, XCircle } from "lucide-react";

export default function ConfirmEmail() {
  const { t } = useLanguage();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading"); // loading | ok | error
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setState("error"); setError(t.verify.invalid); return; }
    api.post("/auth/verify-email", { token })
      .then(() => { setState("ok"); refreshUser(); })
      .catch((e) => { setState("error"); setError(formatApiError(e)); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-20" data-testid="confirm-email-page">
      <FadeIn className="w-full max-w-md text-center">
        <Overline className="text-center">{t.verify.overline}</Overline>
        {state === "loading" && <p className="mt-8 text-ink/50">{t.common.loading}</p>}
        {state === "ok" && (
          <div data-testid="confirm-email-success">
            <CheckCircle2 size={48} className="text-ember mx-auto mt-8" />
            <h1 className="mt-8 font-display font-semibold tracking-tighter text-4xl text-ink">{t.verify.successTitle}</h1>
            <p className="mt-4 text-ink/55 text-sm leading-relaxed">{t.verify.successSub}</p>
            <button
              onClick={() => navigate(user ? "/espace-membre" : "/auth")}
              data-testid="confirm-email-continue"
              className="btn-glow mt-8 bg-ink hover:bg-ember text-white px-8 py-4 text-sm tracking-wide transition-colors duration-300"
            >
              {user ? t.verify.goMember : t.auth.loginBtn}
            </button>
          </div>
        )}
        {state === "error" && (
          <div data-testid="confirm-email-error">
            <XCircle size={48} className="text-red-500 mx-auto mt-8" />
            <h1 className="mt-8 font-display font-semibold tracking-tighter text-4xl text-ink">{t.verify.errorTitle}</h1>
            <p className="mt-4 text-ink/55 text-sm leading-relaxed">{error || t.verify.invalid}</p>
            <p className="mt-4 text-ink/50 text-sm">{t.verify.errorSub}</p>
            <Link to="/auth" data-testid="confirm-email-back" className="btn-glow mt-8 inline-block bg-ink hover:bg-ember text-white px-8 py-4 text-sm tracking-wide transition-colors duration-300">
              {t.auth.loginBtn}
            </Link>
          </div>
        )}
      </FadeIn>
    </div>
  );
}
